import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  Switch,
} from "@mui/material";
import Parse from "parse";
import { useNotify } from "react-admin";

const ChatbotDialog = ({ open, onClose }) => {
  const notify = useNotify();
  const [chatbotEnabled, setChatbotEnabled] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const query = new Parse.Query("Settings");
      query.equalTo("type", "chatbot");
      let obj = await query.first({ useMasterKey: true });
      if (obj) {
        setChatbotEnabled(obj.get("settings")?.[0] === "true");
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const query = new Parse.Query("Settings");
      query.equalTo("type", "chatbot");
      let obj = await query.first({ useMasterKey: true });

      if (!obj) {
        const Settings = Parse.Object.extend("Settings");
        obj = new Settings();
        obj.set("type", "chatbot");
      }
      obj.set("settings", [chatbotEnabled.toString()]);
      await obj.save(null, { useMasterKey: true });

      notify("✅ Chatbot settings updated successfully!", { type: "success" });
      onClose();
      
      // Refresh the page to apply chatbot visibility changes
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (err) {
      console.error("Error saving settings:", err);
      notify("❌ Error saving chatbot settings", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: "#2a2a2a",
          borderRadius: "8px",
          color: "white",
          "& .MuiTypography-root": { fontSize: "14px" },
        },
      }}
    >
      <DialogTitle sx={{ color: "white", fontWeight: 600, fontSize: "16px" }}>
        Chatbot Settings
      </DialogTitle>

      <DialogContent dividers sx={{ color: "white" }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            bgcolor="#1f1f1f"
            p={1.5}
            borderRadius={1}
          >
            <Typography color="white">Enable Chatbot</Typography>
            <Switch
              checked={chatbotEnabled}
              onChange={(e) => setChatbotEnabled(e.target.checked)}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            bgcolor: "#333",
            color: "white",
            textTransform: "none",
            px: 4,
            fontSize: "14px",
            "&:hover": { bgcolor: "#444" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          sx={{
            bgcolor: "#FFD600",
            color: "black",
            textTransform: "none",
            px: 4,
            fontWeight: 600,
            fontSize: "14px",
            "&:hover": { bgcolor: "#FFC107" },
          }}
          startIcon={loading && <CircularProgress size={18} color="inherit" />}
        >
          {loading ? "Saving..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatbotDialog;
